import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { agentAPI } from "../services/api";

// Keys that should never be uploaded (local-only overrides)
const LOCAL_ONLY_KEYS = new Set([]);

const AIContext = createContext(null);

// Debounce helper — avoids saving on every keystroke
function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export function AIProvider({ children }) {
  const [models, setModels] = useState([]);
  const [selectedMode, setSelectedMode] = useState("auto");
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [engramApiKey, setEngramApiKeyState] = useState(null);       // user's Engram memory key
  const [engramChatEnabled, setEngramChatEnabledState] = useState(false); // chat memory opt-in

  const [customApiKeys, setCustomApiKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem("customApiKeys") || "{}"); }
    catch { return {}; }
  });

  // disabledModels: ids of models toggled OFF
  const [disabledModels, setDisabledModels] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("disabledModels") || "[]")); }
    catch { return new Set(); }
  });

  // ── Load models + server prefs on mount ─────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("authToken");

    async function load() {
      try {
        if (token) {
          // Authenticated: fetch models + prefs + saved keys in one call
          const data = await agentAPI.getModelsWithPrefs();
          setModels(data.models || []);
          if (data.disabledModels?.length) setDisabledModels(new Set(data.disabledModels));
          if (data.selectedModelId) setSelectedModelId(data.selectedModelId);
          if (data.selectedMode) setSelectedMode(data.selectedMode);
          // Merge server keys into localStorage keys (server wins for non-empty values)
          if (data.customApiKeys && Object.keys(data.customApiKeys).length) {
            setCustomApiKeys(prev => ({ ...prev, ...data.customApiKeys }));
          }
          // Load Engram key from server (not stored in localStorage)
          if (data.engramApiKey) setEngramApiKeyState(data.engramApiKey);
          // Load Engram chat toggle from server
          setEngramChatEnabledState(data.engramChatEnabled ?? false);
        } else {
          // Unauthenticated: just fetch model list
          const data = await agentAPI.getModels();
          setModels(data.models || []);
        }
      } catch (err) {
        console.error("[AIContext] Error loading models/prefs:", err);
        // Fallback: load just the model list
        try {
          const data = await agentAPI.getModels();
          setModels(data.models || []);
        } catch { /* ignore */ }
      } finally {
        setLoading(false);
        setPrefsLoaded(true);
      }
    }

    load();
  }, []); // only on mount

  // ── Persist customApiKeys to localStorage + server ───────────────────────
  useEffect(() => {
    localStorage.setItem("customApiKeys", JSON.stringify(customApiKeys));
  }, [customApiKeys]);

  const saveKeysToServer = useCallback(async (keys) => {
    const token = localStorage.getItem("authToken");
    if (!token || !prefsLoaded) return;
    const toSave = Object.fromEntries(
      Object.entries(keys).filter(([k]) => !LOCAL_ONLY_KEYS.has(k))
    );
    try {
      await agentAPI.saveCustomApiKeys(toSave);
    } catch (err) {
      console.warn("[AIContext] Failed to save API keys:", err.message);
    }
  }, [prefsLoaded]);

  const debouncedSaveKeys = useDebounce(saveKeysToServer, 1000);

  useEffect(() => {
    if (!prefsLoaded) return;
    debouncedSaveKeys(customApiKeys);
  }, [customApiKeys]); // eslint-disable-line

  // ── Save model prefs to server (debounced 1.5s) ──────────────────────────
  const savePrefsToServer = useCallback(async (disabled, modelId, mode) => {
    const token = localStorage.getItem("authToken");
    if (!token || !prefsLoaded) return;
    try {
      await agentAPI.saveModelPreferences({
        disabledModels: [...disabled],
        selectedModelId: modelId || null,
        selectedMode: mode || "auto",
      });
    } catch (err) {
      console.warn("[AIContext] Failed to save prefs:", err.message);
    }
  }, [prefsLoaded]);

  const debouncedSave = useDebounce(savePrefsToServer, 1500);

  // Mirror disabledModels to localStorage + server whenever it changes
  useEffect(() => {
    if (!prefsLoaded) return;
    localStorage.setItem("disabledModels", JSON.stringify([...disabledModels]));
    debouncedSave(disabledModels, selectedModelId, selectedMode);
  }, [disabledModels]); // eslint-disable-line

  // Mirror selectedModelId / selectedMode to server
  useEffect(() => {
    if (!prefsLoaded) return;
    debouncedSave(disabledModels, selectedModelId, selectedMode);
  }, [selectedModelId, selectedMode]); // eslint-disable-line

  // ── Helper functions ────────────────────────────────────────────────────
  // Saving a (non-empty) key now goes through the server's live-verification
  // check (PUT /custom-api-keys rejects keys that don't actually work) — so
  // this awaits the save and only reflects it in local state on success,
  // rather than optimistically showing the key as "saved" before knowing
  // whether the server accepted it. Callers should catch the rejection to
  // surface the specific error (e.g. ModelRow's onBlur save).
  async function setCustomKey(modelId, key) {
    const token = localStorage.getItem("authToken");
    // Deletions (empty key) and local-only keys (not real provider keys —
    // never verified) can still apply immediately.
    if (!key || LOCAL_ONLY_KEYS.has(modelId)) {
      setCustomApiKeys((prev) => ({ ...prev, [modelId]: key }));
      if (token && prefsLoaded && !LOCAL_ONLY_KEYS.has(modelId)) {
        agentAPI.saveCustomApiKeys({ [modelId]: key }).catch(err =>
          console.warn("[AIContext] Key save failed:", err.message)
        );
      }
      return;
    }
    if (!(token && prefsLoaded)) {
      setCustomApiKeys((prev) => ({ ...prev, [modelId]: key }));
      return;
    }
    await agentAPI.saveCustomApiKeys({ [modelId]: key }); // throws on verification failure — let the caller handle it
    setCustomApiKeys((prev) => ({ ...prev, [modelId]: key }));
  }

  /** Save (or clear) user's Engram API key to server */
  async function setEngramKey(key) {
    setEngramApiKeyState(key || null);
    try {
      await agentAPI.saveEngramKey(key || "");
    } catch (err) {
      console.warn("[AIContext] Engram key save failed:", err.message);
    }
  }

  /** Toggle Engram memory for chat sessions (opt-in) */
  async function setEngramChatEnabled(enabled) {
    setEngramChatEnabledState(!!enabled);
    try {
      await agentAPI.saveEngramSettings({ engramChatEnabled: !!enabled });
    } catch (err) {
      console.warn("[AIContext] Engram settings save failed:", err.message);
    }
  }

  function toggleModel(modelId) {
    setDisabledModels((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });
  }

  function isModelEnabled(modelId) {
    return !disabledModels.has(modelId);
  }

  /** Returns enabled models (for dropdown in SpecializedAgentsPage) */
  function getEnabledModels() {
    return models.filter((m) => isModelEnabled(m.id));
  }

  /** Gets selectedModel object — useful for display */
  function getSelectedModel() {
    return models.find((m) => m.id === selectedModelId) || null;
  }

  function getAgentOptions() {
    return {
      mode: selectedMode,
      modelId: selectedModelId || undefined,
      customApiKey:
        selectedModelId ? customApiKeys[selectedModelId] : undefined,
      disabledModels: disabledModels.size > 0 ? [...disabledModels] : undefined,
    };
  }

  return (
    <AIContext.Provider
      value={{
        models,
        loading,
        prefsLoaded,
        selectedMode,
        setSelectedMode,
        selectedModelId,
        setSelectedModelId,
        customApiKeys,
        setCustomKey,
        disabledModels,
        toggleModel,
        isModelEnabled,
        getEnabledModels,
        getSelectedModel,
        getAgentOptions,
        engramApiKey,
        setEngramKey,
        engramChatEnabled,
        setEngramChatEnabled,
        refreshModels: async () => {
          try {
            const token = localStorage.getItem("authToken");
            if (token) {
              const data = await agentAPI.getModelsWithPrefs();
              setModels(data.models || []);
              if (data.customApiKeys && Object.keys(data.customApiKeys).length) {
                setCustomApiKeys(prev => ({ ...prev, ...data.customApiKeys }));
              }
            } else {
              const data = await agentAPI.getModels();
              setModels(data.models || []);
            }
          } catch (err) {
            console.warn('[AIContext] refreshModels failed:', err.message);
          }
        },
      }}
    >
      {children}
    </AIContext.Provider>
  );
}

export const useAI = () => {
  const ctx = useContext(AIContext);
  if (!ctx) throw new Error("useAI must be used within AIProvider");
  return ctx;
};
